import { useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams, Link } from "react-router-dom";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Receipt,
  CreditCard,
  FileDown,
  Gauge,
  PiggyBank,
  GraduationCap,
  Printer,
  Scale,
  Scissors,
  XCircle,
} from "lucide-react";

import { ApiError, formatCLP, formatDate, formatDateTime } from "../../lib/api";
import { hasPermission } from "../../lib/auth";
import { useToast } from "../../components/Toast";
import { SemaforoDeuda, diasDesdeVencimiento } from "../../components/SemaforoDeuda";
import {
  Badge,
  type BadgeTone,
  Card,
  Cargando,
  ErrorCarga,
  PageHeader,
  TableWrap,
  type OrigenNavegacion as Desde,
} from "../../components/ui";
import { useAuth } from "../auth/useAuth";
import {
  aplicarSaldoFavor,
  descargarBoletaPdf,
  descargarReciboPdf,
  fetchResumenCuenta,
  fetchContratos,
  fetchLecturasPendientesSocio,
  fetchMultasSocio,
  fetchPagosSocio,
  fetchSubsidioVigente,
  type ContratoOperativo,
  obtenerSocio,
} from "./api";
import { formatRut } from "../../lib/rut";
import { RegistrarPagoForm } from "./RegistrarPagoForm";
import { AnularPagoButton } from "./AnularPagoButton";
import { refrescarTrasMovimientoDePago } from "./refrescar";
import { listarPorContrato, type OrdenCorteRead } from "../cortes/api";
import { AjustesSocioSection } from "../ajustes/AjustesSocioSection";
import { EstadoCorteBadge } from "../cortes/estado";

/** Estados de una orden de corte que todavía no termina. */
const ESTADOS_CORTE_VIVOS = new Set(["Pendiente", "Notificada", "EnRuta", "Cortada"]);

export function CuentaCorrienteSocioPage(): JSX.Element {
  const { socioId: socioIdParam } = useParams<{ socioId: string }>();
  const socioId = Number(socioIdParam);
  const [params] = useSearchParams();
  const location = useLocation();
  const { state: auth } = useAuth();
  const puedeGestionarCortes = hasPermission(auth, "cortes.gestionar");
  const contratoIdHint = params.get("contrato_id");
  // `?accion=pagar` lo manda "Registrar pago" de la Caja; `?accion=imprimir`
  // lo manda el ícono de impresora de los deudores del Inicio.
  const accion = params.get("accion");
  // Para que /cortes pueda volver acá con la misma convención.
  const desdeEsta: Desde = {
    etiqueta: "Cuenta corriente",
    ruta: `${location.pathname}${location.search}`,
  };

  const contratosQ = useQuery({
    queryKey: ["contratos"],
    queryFn: fetchContratos,
  });

  // Recorrido F7: un socio puede tener varias casas. La cabecera las
  // muestra todas y el subsidio y los cortes siguen al contrato elegido.
  const socioQ = useQuery({
    queryKey: ["socio", socioId],
    queryFn: () => obtenerSocio(socioId),
    enabled: Number.isFinite(socioId),
  });
  const contratosSocio: ContratoOperativo[] =
    contratosQ.data?.filter((c) => c.socio_id === socioId) ?? [];
  const [contratoElegido, setContratoElegido] = useState<number | null>(
    contratoIdHint ? Number(contratoIdHint) : null,
  );
  const contrato: ContratoOperativo | undefined =
    contratosSocio.find((c) => c.id === contratoElegido) ?? contratosSocio[0];

  const pendientesQ = useQuery({
    queryKey: ["lecturas-pendientes", socioId],
    queryFn: () => fetchLecturasPendientesSocio(socioId),
    enabled: Number.isFinite(socioId),
  });

  const pagosQ = useQuery({
    queryKey: ["pagos-socio", socioId],
    queryFn: () => fetchPagosSocio(socioId),
    enabled: Number.isFinite(socioId),
  });

  const multasQ = useQuery({
    queryKey: ["multas-socio", socioId],
    queryFn: () => fetchMultasSocio(socioId),
    enabled: Number.isFinite(socioId),
  });

  const subsidioQ = useQuery({
    queryKey: ["subsidio-vigente", contrato?.id],
    queryFn: () => fetchSubsidioVigente(contrato!.id),
    enabled: contrato !== undefined,
  });

  const resumenQ = useQuery({
    queryKey: ["resumen-cuenta", socioId],
    queryFn: () => fetchResumenCuenta(socioId),
    enabled: Number.isFinite(socioId),
  });

  const cortesQ = useQuery({
    queryKey: ["ordenes-corte-contrato", contrato?.id],
    queryFn: () => listarPorContrato(contrato!.id, true),
    enabled: contrato !== undefined,
  });

  // Órdenes vivas de TODAS las casas del socio, no sólo de la elegida: si
  // alguna está cortada, la administrativa tiene que verlo apenas abre la
  // ficha para reponer al cobrar (auditoría UX-14).
  const cortesTodosQ = useQueries({
    queries: contratosSocio.map((c) => ({
      queryKey: ["ordenes-corte-contrato", c.id, "vivas"],
      queryFn: () => listarPorContrato(c.id, false),
    })),
  });
  const cortesVivos: { orden: OrdenCorteRead; contrato: ContratoOperativo }[] = [];
  cortesTodosQ.forEach((q, i) => {
    const c = contratosSocio[i];
    if (!c) return;
    for (const o of q.data ?? []) {
      if (ESTADOS_CORTE_VIVOS.has(o.estado)) cortesVivos.push({ orden: o, contrato: c });
    }
  });
  const hayCorteVivo = cortesVivos.length > 0;
  const contratoTieneOrdenViva =
    contrato !== undefined && cortesVivos.some((x) => x.contrato.id === contrato.id);

  // Traer a la vista la sección que pidió quien enlazó.
  const destino =
    accion === "imprimir"
      ? "boletas-pendientes"
      : location.hash === "#cortes"
        ? "cortes"
        : null;
  const listoParaScroll = destino === "cortes" ? !cortesQ.isPending : !pendientesQ.isPending;
  useEffect(() => {
    if (!destino || !listoParaScroll) return;
    document.getElementById(destino)?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [destino, listoParaScroll]);

  if (!Number.isFinite(socioId)) {
    return <p className="text-sm text-rose-700">Socio inválido.</p>;
  }

  const totalPendiente = pendientesQ.data?.reduce(
    (acc, l) => acc + (l.saldo_actual ?? l.monto_total),
    0,
  ) ?? 0;
  const totalMultasPendientes = multasQ.data
    ?.filter((m) => m.estado === "Pendiente")
    .reduce((acc, m) => acc + m.monto, 0) ?? 0;

  const etiquetaArranque = (c: ContratoOperativo): string =>
    `Arranque ${c.arranque_codigo ?? "—"} · ${c.arranque_direccion}`;

  const seccionCortes = (
    <Section
      id="cortes"
      title="Órdenes de corte"
      icon={<Scissors className="h-4 w-4" strokeWidth={1.5} />}
      right={
        contrato && (
          <div className="flex flex-wrap items-center justify-end gap-3">
            {puedeGestionarCortes && !contratoTieneOrdenViva && (
              <Link
                to={`/cortes?nueva=1&contrato=${contrato.id}`}
                state={{ desde: desdeEsta }}
                className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1 text-xs font-medium text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50"
                title={`Abrir una orden de corte para ${etiquetaArranque(contrato)}`}
              >
                <Scissors className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
                Abrir corte de este arranque
              </Link>
            )}
            <Link
              to="/cortes"
              className="text-xs font-medium text-brand-700 hover:text-brand-800"
            >
              Ir a Cortes →
            </Link>
          </div>
        )
      }
    >
      {contratosSocio.length > 1 && contrato && (
        <p className="mb-3 text-xs text-neutral-500">
          Mostrando {etiquetaArranque(contrato)}. Elige otra casa arriba para ver
          o abrir su corte.
        </p>
      )}
      {cortesQ.isPending ? (
        <Cargando inline />
      ) : cortesQ.isError ? (
        <ErrorCarga error={cortesQ.error} onReintentar={() => void cortesQ.refetch()} />
      ) : (cortesQ.data ?? []).length === 0 ? (
        <p className="text-sm text-neutral-500">
          Sin órdenes de corte para este arranque.
        </p>
      ) : (
        <TableWrap label="Órdenes de corte">
        <table className="min-w-full divide-y divide-neutral-200 text-sm">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th scope="col" className="px-3 py-2 text-left">Generada</th>
              <th scope="col" className="px-3 py-2 text-right">Deuda al corte</th>
              <th scope="col" className="px-3 py-2 text-left">Estado</th>
              <th scope="col" className="px-3 py-2 text-right">{""}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {cortesQ.data!.map((o) => (
              <tr key={o.id}>
                <td className="px-3 py-2 text-neutral-600">
                  {formatDate(o.fecha_generacion)}
                </td>
                <td className="px-3 py-2 text-right font-medium">
                  {formatCLP(o.monto_deuda_al_corte)}
                </td>
                <td className="px-3 py-2">
                  <EstadoCorteBadge estado={o.estado} />
                </td>
                <td className="px-3 py-2 text-right">
                  <Link
                    to={`/cortes/${o.id}`}
                    state={{ desde: desdeEsta }}
                    className="text-xs font-medium text-brand-700 hover:text-brand-800"
                  >
                    Detalle →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableWrap>
      )}
    </Section>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          socioQ.data ? `Cuenta corriente de ${socioQ.data.nombre}` : "Cuenta corriente"
        }
        subtitle={socioQ.data ? `RUT ${formatRut(socioQ.data.rut)}` : undefined}
        volver={{ etiqueta: "Socios", ruta: "/socios" }}
        actions={
          socioQ.data &&
          pendientesQ.data && (
            <SemaforoDeuda
              meses={null}
              dias={diasDesdeVencimiento(
                pendientesQ.data.map((l) => l.fecha_vencimiento),
              )}
              monto={totalPendiente}
            />
          )
        }
      />

      <Card padding="lg">
        {socioQ.isPending ? (
          <Cargando inline texto="Cargando socio…" />
        ) : socioQ.isError ? (
          <ErrorCarga error={socioQ.error} onReintentar={() => void socioQ.refetch()} />
        ) : socioQ.data ? (
          <div>
            {contratosSocio.length === 0 ? (
              <p className="text-sm text-neutral-600">
                Sin contratos vigentes.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {contratosSocio.map((c) => {
                  const activo = c.id === contrato?.id;
                  const cortada = cortesVivos.some(
                    (x) => x.contrato.id === c.id && x.orden.estado === "Cortada",
                  );
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setContratoElegido(c.id)}
                      disabled={contratosSocio.length === 1}
                      aria-pressed={activo}
                      className={`rounded-md border px-3 py-1.5 text-left text-sm ${
                        activo
                          ? "border-brand-500 bg-brand-50 text-neutral-900"
                          : "border-neutral-200 text-neutral-600 hover:border-neutral-300"
                      }`}
                    >
                      {etiquetaArranque(c)}
                      {c.sector_nombre && (
                        <span className="text-neutral-500"> · {c.sector_nombre}</span>
                      )}
                      {cortada && (
                        <span className="ml-1 font-medium text-rose-700"> · cortado</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {contratosSocio.length > 1 && (
              <p className="mt-2 text-xs text-neutral-500">
                Tiene {contratosSocio.length} casas. Las boletas, pagos y el
                saldo son del socio; el subsidio y los cortes son del arranque
                elegido.
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-amber-700">Socio no encontrado en el padrón.</p>
        )}
      </Card>

      {hayCorteVivo && <AvisoCortes cortes={cortesVivos} desde={desdeEsta} />}

      {accion === "imprimir" && (
        <p
          role="status"
          className="flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-800"
        >
          <Printer className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
          Para imprimir, usa el botón &ldquo;Boleta&rdquo; de la boleta que
          necesitas en la lista de boletas pendientes.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard
          icon={<Receipt className="h-5 w-5" strokeWidth={1.5} />}
          label="Boletas pendientes"
          value={
            pendientesQ.isPending
              ? "…"
              : `${pendientesQ.data?.length ?? 0} boletas — ${formatCLP(totalPendiente)}`
          }
          tone={totalPendiente > 0 ? "warn" : "ok"}
        />
        <SummaryCard
          icon={<AlertTriangle className="h-5 w-5" strokeWidth={1.5} />}
          label="Multas pendientes"
          value={
            multasQ.isPending
              ? "…"
              : `${
                  multasQ.data?.filter((m) => m.estado === "Pendiente").length ?? 0
                } multas — ${formatCLP(totalMultasPendientes)}`
          }
          tone={totalMultasPendientes > 0 ? "warn" : "ok"}
        />
        <SummaryCard
          icon={<GraduationCap className="h-5 w-5" strokeWidth={1.5} />}
          label="Subsidio vigente"
          value={
            subsidioQ.isPending
              ? "…"
              : subsidioQ.data
              ? `${subsidioQ.data.porcentaje_subsidio}% hasta ${subsidioQ.data.tope_m3} m³`
              : "Sin subsidio"
          }
          tone={subsidioQ.data ? "info" : "muted"}
        />
        <SaldoFavorCard socioId={socioId} saldo={resumenQ.data?.saldo_a_favor ?? 0} />
      </div>

      {contrato && (
        <RegistrarPagoForm
          socioId={socioId}
          contratoId={contrato.id}
          totalPendiente={totalPendiente}
          saldoAFavor={resumenQ.data?.saldo_a_favor ?? 0}
          enfocar={accion === "pagar"}
        />
      )}

      {hayCorteVivo && seccionCortes}

      <Section
        id="boletas-pendientes"
        title="Boletas pendientes"
        icon={<Gauge className="h-4 w-4" strokeWidth={1.5} />}
        destacada={accion === "imprimir"}
      >
        {pendientesQ.isPending ? (
          <Cargando inline />
        ) : pendientesQ.isError ? (
          <ErrorCarga error={pendientesQ.error} onReintentar={() => void pendientesQ.refetch()} />
        ) : (pendientesQ.data ?? []).length === 0 ? (
          <p className="text-sm text-emerald-700">
            Sin saldo pendiente. ✓
          </p>
        ) : (
          <TableWrap label="Boletas pendientes">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Periodo</th>
                <th scope="col" className="px-3 py-2 text-left">Vencimiento</th>
                <th scope="col" className="px-3 py-2 text-right">Consumo</th>
                <th scope="col" className="px-3 py-2 text-right">Facturado</th>
                <th scope="col" className="px-3 py-2 text-right">Saldo</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right">
                  <span className="sr-only">Imprimir</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {pendientesQ.data!.map((l) => {
                const saldo = l.saldo_actual ?? l.monto_total;
                const parcial =
                  l.saldo_actual !== undefined && l.saldo_actual < l.monto_total;
                return (
                  <tr key={l.id}>
                    <td className="px-3 py-2">{`${String(l.mes).padStart(2, "0")}/${l.anio}`}</td>
                    <td className="px-3 py-2 text-neutral-600">
                      {l.fecha_vencimiento ? formatDate(l.fecha_vencimiento) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">{l.consumo_m3} m³</td>
                    <td className="px-3 py-2 text-right text-neutral-600">
                      {formatCLP(l.monto_total)}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {formatCLP(saldo)}
                      {parcial && (
                        <span className="ml-1 text-xs text-amber-700">pago parcial</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <LecturaEstadoBadge estado={l.estado} />
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <BoletaButton
                        lecturaId={l.id}
                        numero={l.numero_boleta}
                        periodo={`${String(l.mes).padStart(2, "0")}/${l.anio}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </TableWrap>
        )}
      </Section>

      <Section title="Pagos" icon={<CreditCard className="h-4 w-4" strokeWidth={1.5} />}>
        {pagosQ.isPending ? (
          <Cargando inline />
        ) : pagosQ.isError ? (
          <ErrorCarga error={pagosQ.error} onReintentar={() => void pagosQ.refetch()} />
        ) : (pagosQ.data ?? []).length === 0 ? (
          <p className="text-sm text-neutral-500">Sin pagos registrados.</p>
        ) : (
          <TableWrap label="Pagos">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Fecha</th>
                <th scope="col" className="px-3 py-2 text-left">Forma</th>
                <th scope="col" className="px-3 py-2 text-left">Comprobante</th>
                <th scope="col" className="px-3 py-2 text-right">Monto</th>
                <th scope="col" className="px-3 py-2 text-left">Aplicado a</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right">
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {pagosQ.data!.map((p) => {
                const aplicado = p.detalles.reduce(
                  (acc, d) => acc + d.monto_asignado,
                  0,
                );
                const saldoFavor = Math.max(0, p.monto_pagado - aplicado);
                const esAplicacion = p.forma_pago === "Saldo a Favor";
                return (
                  <tr key={p.id} className={esAplicacion ? "bg-emerald-50/40" : undefined}>
                    <td className="px-3 py-2 text-neutral-600">
                      {formatDateTime(p.fecha_pago)}
                    </td>
                    <td className="px-3 py-2">
                      {esAplicacion ? (
                        <span className="inline-flex items-center gap-1 text-emerald-800">
                          <PiggyBank className="h-3.5 w-3.5" strokeWidth={1.5} />
                          Aplicación de saldo a favor
                        </span>
                      ) : (
                        p.forma_pago
                      )}
                    </td>
                    <td className="px-3 py-2 text-neutral-600">
                      {p.nro_comprobante ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {formatCLP(esAplicacion ? aplicado : p.monto_pagado)}
                    </td>
                    <td className="px-3 py-2 text-neutral-600">
                      {p.detalles.length} boleta(s)
                      {saldoFavor > 0 && (
                        <span className="ml-1 text-xs text-amber-700">
                          (+{formatCLP(saldoFavor)} a favor)
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {p.estado === "Activo" ? (
                        <Badge tone="emerald">Activo</Badge>
                      ) : (
                        <span title={p.motivo_anulacion ?? undefined}>
                          <Badge tone="rose">Anulado</Badge>
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <ReciboButton pagoId={p.id} />
                      {p.estado === "Activo" && (
                        <AnularPagoButton
                          pagoId={p.id}
                          socioId={socioId}
                          monto={esAplicacion ? aplicado : p.monto_pagado}
                          fecha={p.fecha_pago}
                          forma={p.forma_pago}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </TableWrap>
        )}
      </Section>

      <Section title="Multas" icon={<XCircle className="h-4 w-4" strokeWidth={1.5} />}>
        {multasQ.isPending ? (
          <Cargando inline />
        ) : multasQ.isError ? (
          <ErrorCarga error={multasQ.error} onReintentar={() => void multasQ.refetch()} />
        ) : (multasQ.data ?? []).length === 0 ? (
          <p className="text-sm text-neutral-500">Sin multas.</p>
        ) : (
          <TableWrap label="Multas">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Fecha</th>
                <th scope="col" className="px-3 py-2 text-left">Origen</th>
                <th scope="col" className="px-3 py-2 text-left">Motivo</th>
                <th scope="col" className="px-3 py-2 text-right">Monto</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {multasQ.data!.map((m) => (
                <tr key={m.id}>
                  <td className="px-3 py-2 text-neutral-600">
                    {formatDate(m.fecha_generacion)}
                  </td>
                  <td className="px-3 py-2">{m.origen}</td>
                  <td className="px-3 py-2 text-neutral-700">{m.motivo}</td>
                  <td className="px-3 py-2 text-right font-medium">
                    {formatCLP(m.monto)}
                  </td>
                  <td className="px-3 py-2">
                    <MultaEstadoBadge estado={m.estado} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableWrap>
        )}
      </Section>

      <Section
        title="Ajustes de cuenta"
        icon={<Scale className="h-4 w-4" strokeWidth={1.5} />}
      >
        <AjustesSocioSection socioId={socioId} contratoId={contrato?.id} />
      </Section>

      {!hayCorteVivo && seccionCortes}
    </div>
  );
}

/** Aviso arriba de la ficha cuando alguna casa del socio está cortada o con
 *  una orden de corte en curso (auditoría UX-14). */
function AvisoCortes({
  cortes,
  desde,
}: {
  cortes: { orden: OrdenCorteRead; contrato: ContratoOperativo }[];
  desde: Desde;
}): JSX.Element {
  const algunaCortada = cortes.some((x) => x.orden.estado === "Cortada");
  return (
    <div
      role="status"
      className={`rounded-lg border px-4 py-3 text-sm ${
        algunaCortada
          ? "border-rose-200 bg-rose-50 text-rose-800"
          : "border-amber-200 bg-amber-50 text-amber-800"
      }`}
    >
      <div className="flex items-start gap-2">
        <Scissors className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
        <ul className="space-y-1">
          {cortes.map(({ orden, contrato }) => (
            <li key={orden.id}>
              {orden.estado === "Cortada" ? (
                <>
                  <strong>Servicio cortado</strong> en el arranque{" "}
                  {contrato.arranque_codigo ?? "—"} ({contrato.arranque_direccion}).
                  Cuando pague, repón el servicio desde la orden.{" "}
                </>
              ) : (
                <>
                  <strong>Orden de corte en curso</strong> (
                  {orden.estado === "Pendiente"
                    ? "abierta"
                    : orden.estado === "EnRuta"
                      ? "en ruta"
                      : "notificada"}
                  ) en el arranque {contrato.arranque_codigo ?? "—"} (
                  {contrato.arranque_direccion}).{" "}
                </>
              )}
              <Link
                to={`/cortes/${orden.id}`}
                state={{ desde }}
                className="font-medium underline underline-offset-2 hover:no-underline"
              >
                {orden.estado === "Cortada" ? "Ir a la orden para reponer" : "Ver la orden"}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Section({
  id,
  title,
  icon,
  right,
  destacada = false,
  children,
}: {
  id?: string;
  title: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  destacada?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section
      id={id}
      className={`scroll-mt-4 bg-white rounded-lg border ${
        destacada ? "border-brand-500 ring-1 ring-brand-500" : "border-neutral-200"
      }`}
    >
      <header className="px-4 py-3 border-b border-neutral-100 flex flex-wrap items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
        {right && <div className="ml-auto">{right}</div>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function SaldoFavorCard({
  socioId,
  saldo,
}: {
  socioId: number;
  saldo: number;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const mutation = useMutation({
    mutationFn: () => aplicarSaldoFavor(socioId),
    onSuccess: (pago) => {
      toast.success(
        pago
          ? `Saldo a favor aplicado (${pago.detalles.length} boleta(s))`
          : "No hay boletas pendientes a las que aplicar el saldo",
      );
      refrescarTrasMovimientoDePago(qc, socioId);
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo aplicar"),
  });
  return (
    <div
      className={`rounded-lg border p-4 ${
        saldo > 0
          ? "bg-emerald-50 border-emerald-200 text-emerald-900"
          : "bg-neutral-50 border-neutral-200 text-neutral-600"
      }`}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80">
        <PiggyBank className="h-5 w-5" strokeWidth={1.5} />
        Saldo a favor
      </div>
      <div className="mt-1 text-lg font-semibold">{formatCLP(saldo)}</div>
      {saldo > 0 && (
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
          className="mt-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
          title="Se aplica solo al facturar y al registrar pagos; esto lo fuerza ahora"
        >
          {mutation.isPending ? "Aplicando…" : "Aplicar a boletas pendientes"}
        </button>
      )}
    </div>
  );
}

function BoletaButton({
  lecturaId,
  numero,
  periodo,
}: {
  lecturaId: number;
  numero: string | null;
  periodo: string;
}): JSX.Element {
  const toast = useToast();
  const [pending, setPending] = useState(false);
  async function descargar(): Promise<void> {
    setPending(true);
    try {
      await descargarBoletaPdf(lecturaId, numero);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "No se pudo generar la boleta");
    } finally {
      setPending(false);
    }
  }
  return (
    <button
      type="button"
      onClick={() => void descargar()}
      disabled={pending}
      aria-label={`Descargar boleta de ${periodo} (PDF)`}
      title="Boleta (PDF) para imprimir"
      className="inline-flex items-center gap-1 text-xs text-neutral-600 hover:text-brand-700 disabled:opacity-50"
    >
      <FileDown className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
      {pending ? "Generando…" : "Boleta"}
    </button>
  );
}

function ReciboButton({ pagoId }: { pagoId: number }): JSX.Element {
  const toast = useToast();
  const [pending, setPending] = useState(false);
  async function descargar(): Promise<void> {
    setPending(true);
    try {
      await descargarReciboPdf(pagoId);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "No se pudo generar el recibo");
    } finally {
      setPending(false);
    }
  }
  return (
    <button
      type="button"
      onClick={() => void descargar()}
      disabled={pending}
      title="Recibo de pago (PDF)"
      className="mr-2 inline-flex items-center gap-1 text-xs text-neutral-600 hover:text-brand-700 disabled:opacity-50"
    >
      <FileDown className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
      Recibo
    </button>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: "ok" | "warn" | "info" | "muted";
}): JSX.Element {
  const map: Record<typeof tone, string> = {
    ok: "bg-emerald-50 text-emerald-800 border-emerald-100",
    warn: "bg-amber-50 text-amber-800 border-amber-100",
    info: "bg-brand-50 text-brand-800 border-brand-100",
    muted: "bg-neutral-50 text-neutral-600 border-neutral-100",
  };
  return (
    <div className={`rounded-lg border p-4 ${map[tone]}`}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider opacity-80">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-base font-semibold">{value}</div>
    </div>
  );
}

function LecturaEstadoBadge({ estado }: { estado: string }): JSX.Element {
  const map: Record<string, BadgeTone> = {
    Pendiente: "slate",
    Emitida: "brand",
    Pagada: "emerald",
    Vencida: "rose",
    Anulada: "slate",
  };
  return <Badge tone={map[estado] ?? "slate"}>{estado}</Badge>;
}

function MultaEstadoBadge({ estado }: { estado: string }): JSX.Element {
  const map: Record<string, BadgeTone> = {
    Pendiente: "amber",
    Facturada: "emerald",
    Anulada: "slate",
  };
  return <Badge tone={map[estado] ?? "slate"}>{estado}</Badge>;
}

